import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./EnterpriseAuthContext";
import { getAiTier } from "../services/usageService";
import aiService from "../services/aiService";
import {
  getModelDisplayName,
  getPlanModelId,
  MODEL_IDS,
} from "../constants/aiModels";

// Action types
const ActionTypes = {
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  SET_SETTINGS: "SET_SETTINGS",
  UPDATE_SETTING: "UPDATE_SETTING",
};

// Initial state with Gemini defaults for general users
// Note: Gemini API key is managed server-side via Edge Functions. `defaultModel` is derived
// from the plan (display only); the server picks the real model per task and tier.
const initialState = {
  openaiApiKey: "", // Not used in Option A, kept for future Option B pivot
  anthropicApiKey: "", // Not used in Option A, kept for future Option B pivot  
  googleApiKey: "", // No client-side Gemini key
  defaultModel: MODEL_IDS.fast,
  theme: "light",
  loading: false,
  error: null,
};

/**
 * Reduce settings state transitions.
 * @param {object} state - Current settings state.
 * @param {object} action - Action with type and payload.
 * @returns {object} Updated state.
 */
function settingsReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };

    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case ActionTypes.CLEAR_ERROR:
      return { ...state, error: null };

    case ActionTypes.SET_SETTINGS:
      return { ...state, ...action.payload, loading: false };

    case ActionTypes.UPDATE_SETTING:
      return { ...state, [action.payload.key]: action.payload.value };

    default:
      return state;
  }
}

// Create context
const SettingsContext = createContext();

/**
 * Settings context provider.
 * @param {object} props - Provider props.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element} Provider wrapper.
 */
export function SettingsProvider({ children }) {
  const { state: authState } = useAuth();
  const [state, dispatch] = useReducer(settingsReducer, initialState);
  /** Tier the server uses for model selection (plan_name only). */
  const aiTier = getAiTier(authState.currentMembership);
  const planModelId = getPlanModelId(aiTier);

  // Default account for AI tier resolution: calls that do not pass `accountId` (e.g. Inky's
  // draft/patch helpers) still resolve the active account's plan on the server.
  const activeAccountId =
    authState.activeAccount?.id || authState.currentMembership?.account_id || null;
  useEffect(() => {
    aiService.setActiveAccountId(authState.isAuthenticated ? activeAccountId : null);
  }, [activeAccountId, authState.isAuthenticated]);

  // Load settings from Supabase
  const loadSettings = useCallback(async () => {
    if (!authState.user || !authState.isAuthenticated) {
      return;
    }

    dispatch({ type: ActionTypes.SET_LOADING, payload: true });

    try {
      const { data, error } = await supabase
        .from("settings")
        .select("*")
        .eq("user_id", authState.user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        // Ignore 'not found' errors
        throw error;
      }

      // The model is plan-derived (Gemini 4 Argon for Pro / Pro Plus / Enterprise, Gemini 3.7
      // Flash for Free). Stored legacy ids (2.5 / 3.x previews) are rewritten once.
      const tierSyncedModel = getPlanModelId(getAiTier(authState.currentMembership));
      const defaultModel = tierSyncedModel;
      const needsTierUpsert = data?.default_model !== tierSyncedModel;

      // Always ensure we have working defaults, regardless of what's in the database
      const ensuredSettings = {
        openaiApiKey: data?.openai_api_key || "",
        anthropicApiKey: data?.anthropic_api_key || "",
        googleApiKey: data?.google_api_key || "",
        defaultModel,
        theme: data?.theme || "light",
      };

      dispatch({ type: ActionTypes.SET_SETTINGS, payload: ensuredSettings });

      // New row, plan tier changed, or deprecated Gemini id in DB → persist default_model
      if (!data || needsTierUpsert) {
        try {
          await supabase.from("settings").upsert(
            {
              user_id: authState.user.id,
              openai_api_key: ensuredSettings.openaiApiKey || null,
              anthropic_api_key: ensuredSettings.anthropicApiKey || null,
              google_api_key: ensuredSettings.googleApiKey || null,
              default_model: ensuredSettings.defaultModel,
              theme: ensuredSettings.theme,
            },
            { onConflict: "user_id" }
          );
        } catch (saveError) {
          console.warn("Could not sync settings to database:", saveError);
        }
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  }, [authState.user, authState.isAuthenticated, authState.currentMembership]);

  // Load settings when auth state is resolved
  useEffect(() => {
    if (authState.isAuthenticated && authState.user && !authState.loading) {
      loadSettings();
    } else if (!authState.isAuthenticated && !authState.loading) {
      dispatch({ type: ActionTypes.SET_SETTINGS, payload: initialState });
    }
  }, [
    authState.isAuthenticated,
    authState.user,
    authState.loading,
    loadSettings,
  ]);

  // Save settings to Supabase
  const saveSettings = async (settings) => {
    if (!authState.user) throw new Error("User not authenticated");

    dispatch({ type: ActionTypes.SET_LOADING, payload: true });

    try {
      const settingsData = {
        user_id: authState.user.id,
        openai_api_key: settings.openaiApiKey || null,
        anthropic_api_key: settings.anthropicApiKey || null,
        google_api_key: settings.googleApiKey || null,
        default_model: planModelId,
        theme: settings.theme || "light",
        other_settings: {},
      };

      const { data, error } = await supabase
        .from("settings")
        .upsert(settingsData, { onConflict: "user_id" })
        .select()
        .single();

      if (error) {
        throw error;
      }

      const mappedData = data
        ? {
            openaiApiKey: data.openai_api_key || "",
            anthropicApiKey: data.anthropic_api_key || "",
            googleApiKey: data.google_api_key || "",
            defaultModel: planModelId,
            theme: data.theme || "light",
          }
        : null;

      return { success: true, data: mappedData };
    } catch (error) {
      console.error("Error saving settings:", error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  };

  // Update a single setting
  const updateSetting = async (key, value) => {
    try {
      // Update local state immediately for responsive UI
      dispatch({ type: ActionTypes.UPDATE_SETTING, payload: { key, value } });

      // Save to database
      const newSettings = { ...state, [key]: value };
      await saveSettings(newSettings);
    } catch (error) {
      console.error("Error updating setting:", error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
    }
  };

  // Update multiple settings
  const updateSettings = async (settings) => {
    try {
      // The model is plan-derived; any submitted model id is replaced by the plan model.
      const updatedSettings = { ...settings, defaultModel: planModelId };

      // Update local state immediately for responsive UI
      dispatch({
        type: ActionTypes.SET_SETTINGS,
        payload: { ...state, ...updatedSettings },
      });

      // Save to database
      const finalSettings = { ...state, ...updatedSettings };
      const result = await saveSettings(finalSettings);

      if (!result.success) {
        throw new Error(result.error || "Failed to save settings");
      }

      return result;
    } catch (error) {
      console.error("Error updating settings:", error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: ActionTypes.CLEAR_ERROR });
  };

  // Get API key for a specific service
  const getApiKey = (service) => {
    switch (service) {
      case "openai":
        return state.openaiApiKey;
      case "anthropic":
        return state.anthropicApiKey;
      case "google":
        return state.googleApiKey;
      default:
        return "";
    }
  };

  // Check if any API key is configured (always true with server-managed Gemini access)
  const hasApiKey = () => {
    return true;
  };

  /**
   * Models shown in settings. The model is not user-pickable: it follows the plan and the
   * server routes small tasks (inline edits, quick patches, titles) to the fast model.
   * @returns {Array<{id: string, name: string, provider: string, description: string}>} Plan model.
   */
  const getAvailableModels = () => [
    {
      id: planModelId,
      name: getModelDisplayName(planModelId),
      provider: "google",
      description:
        planModelId === MODEL_IDS.premium
          ? "Premium model for chat, document generation, extraction and summaries"
          : "Fast model for all AI features",
    },
  ];

  const actions = {
    // Settings actions
    updateSetting,
    updateSettings,
    saveSettings,
    loadSettings,

    // Utility actions
    clearError,
    getApiKey,
    hasApiKey,
    getAvailableModels,
  };

  const planModelName = getModelDisplayName(planModelId);
  const stateWithPlan = useMemo(
    () => ({ ...state, aiTier, planModelId, planModelName }),
    [state, aiTier, planModelId, planModelName]
  );

  return (
    <SettingsContext.Provider value={{ state: stateWithPlan, actions }}>
      {children}
    </SettingsContext.Provider>
  );
}

/**
 * Access settings context.
 * @returns {{ state: object, actions: object }} Context value.
 */
export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}

export default SettingsContext;
